import React, { Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { HelmetProvider } from 'react-helmet-async';
import ErrorBoundary from './features/landing/shared/ErrorBoundary';
import LoadingSpinner from './features/landing/shared/LoadingSpinner';
import ScrollRestoration from './features/landing/shared/ScrollRestoration';
import Footer from "./features/landing/shared/Footer";
import BankDetails from './features/mandate/bankDetails';
import GoalsPage from './features/goals/Goals';
import SIPCalculator from './features/landing/shared/calculators/SIPCalculator';
import PPFCalculator from './features/landing/shared/calculators/PPFCalculator';
import PFCalculator from './features/landing/shared/calculators/PFCalculator';
import ReverseSIPCalculator from './features/landing/shared/calculators/ReverseSIPCalculator';
import EmergencyFundCalculator from './features/landing/shared/calculators/EmergencyFundCalculator';
import ChatBotButton from './features/landing/shared/ChatBotButton';
import Navigation from './features/landing/shared/Navigation';
import InvestmentQuizResponses from './pages/InvestmentQuizResponses';

// Lazy load components
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const Login = React.lazy(() => import('../src/features/auth/Login'));
// const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const KYC = React.lazy(() => import('./features/kyc/KYCForm')); 
const RiskProfile = React.lazy(() => import('./features/riskProfile/RiskProfile'));
const SIPTracker = React.lazy(() => import('./pages/SIPTracker'));
const Portfolio = React.lazy(() => import('./pages/Portfolio'));
const Blog = React.lazy(() => import('./pages/Blog'));
const CalculatorPage = React.lazy(() => import('./pages/CalculatorPage'));
const AboutUs = React.lazy(() => import('./pages/AboutUs'));
const ContactUs = React.lazy(() => import('./pages/ContactUs'));
const ErrorPage = React.lazy(() => import('./pages/ErrorPage'));
const Register = React.lazy(() => import('./pages/Register'));
const LeadTracker = React.lazy(() => import('./features/landing/shared/LeadTracker'));
const LeadTable = React.lazy(() => import('./features/landing/shared/LeadTable'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsAndConditions = React.lazy(() => import('./pages/TermsAndConditions'));
const FAQPage = React.lazy(() => import('./pages/FAQPage'));

// Lazy load service pages
const SIPPlanningPage = React.lazy(() => import('./pages/services/SIPPlanningPage'));
const GoalBasedInvestmentPage = React.lazy(() => import('./pages/services/GoalBasedInvestmentPage'));
const RetirementPlanningPage = React.lazy(() => import('./pages/services/RetirementPlanningPage'));
const ChildEducationPage = React.lazy(() => import('./pages/services/ChildEducationPage'));
const PortfolioReviewPage = React.lazy(() => import('./pages/services/PortfolioReviewPage'));
const TaxSavingFundsPage = React.lazy(() => import('./pages/services/TaxSavingFundsPage'));

// Components needed for layout
const PrivateRoute = React.lazy(() => import('./features/landing/shared/PrivateRoute'));
const AdminRoute = React.lazy(() => import('./features/landing/shared/AdminRoute'));
const InvestorDashboard = React.lazy(() => import('./features/dashboard/InvestorDashboard'));
const UserProfile = React.lazy(() => import('./features/profile/UserProfile'));
const YoutubePage = React.lazy(() => import('./pages/YoutubePage'));
const ArticlesPage = React.lazy(() => import('./pages/ArticlesPage'));

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <ErrorBoundary>
          <ScrollRestoration />
          <Suspense fallback={<LoadingSpinner />}>
            <>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/calculators" element={<CalculatorPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/blog/*" element={<Blog />} />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/contact" element={<ContactUs />} />
                <Route path="/register" element={<Register />} />
                {/* <Route path="/job" element={<CreateJobPost />} /> */} 

                {/* Service Routes */}
                <Route path="/services/sip-planning" element={<SIPPlanningPage />} />
                <Route path="/services/goal-based-investment" element={<GoalBasedInvestmentPage />} />
                <Route path="/services/retirement-planning" element={<RetirementPlanningPage />} />
                <Route path="/services/child-education" element={<ChildEducationPage />} />
                <Route path="/services/portfolio-review" element={<PortfolioReviewPage />} />
                <Route path="/services/tax-saving-funds" element={<TaxSavingFundsPage />} />
                <Route  path="/calculators/sip" element={  <SIPCalculator /> }/>     
                <Route  path="/calculators/ppf" element={  <PPFCalculator /> }/>    
                <Route  path="/calculators/pf" element={  <PFCalculator /> }/>   
                <Route  path="/calculators/reverse-sip" element={  <ReverseSIPCalculator  /> }/>  
                <Route  path="/calculators/emergency-fund" element={  <EmergencyFundCalculator  /> }/>                               
                {/* Lead Management Routes - Protected by AdminRoute */}
                <Route
                  path="/leads/add"
                  element={
                    <AdminRoute>
                      <LeadTracker />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/leads/manage"
                  element={
                    <AdminRoute>
                      <LeadTable />
                    </AdminRoute>
                  }
                />
                <Route path="/admin/investment-quiz-responses" element={<InvestmentQuizResponses />} />

                {/* All Routes are Public */}
                {/* <Route
                  path="/dashboard"
                  element={
                    <div>
                      <Navigation />
                      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                        <Dashboard />
                      </main>
                    </div>
                  }
                />*/}
                <Route
                  path="/kyc"
                  element={
                    <div>
                      <Navigation />
                      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                        <KYC />
                      </main>
                    </div>
                  }
                /> 
                <Route
                  path="/bank-details"
                  element={
                    <div>
                      <Navigation />
                      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                        <BankDetails />
                      </main>
                    </div>
                  }
                /> 
                <Route
                  path="/risk-profile"
                  element={
                    <div>
                      <Navigation />
                      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                        <RiskProfile />
                      </main>
                    </div>
                  }
                />
                <Route
                  path="/leads"
                  element={
                    <div>
                      <Navigation />
                      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                        <LeadTracker />
                      </main>
                    </div>
                  }
                />
                <Route
                  path="/sip-tracker"
                  element={
                    <div>
                      <Navigation />
                      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                        <SIPTracker />
                      </main>
                    </div>
                  }
                />
                <Route
                  path="/portfolio"
                  element={
                    <div>
                      <Navigation />
                      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                        <Portfolio />
                      </main>
                    </div>
                  }
                />
                <Route
                  path="/lead-table"
                  element={
                    <div>
                      <Navigation />
                      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                        <LeadTable />
                      </main>
                    </div>
                  }
                />
                <Route
                  path="/privacy-policy"
                  element={
                    <div>
                      <Navigation />
                      <main className="max-w-3xl mx-auto py-8 px-4">
                        <PrivacyPolicy />
                      </main>
                    </div>
                  }
                />
                <Route
                  path="/terms-and-conditions"
                  element={
                    <div>
                      <Navigation />
                      <main className="max-w-3xl mx-auto py-8 px-4">
                        <TermsAndConditions />
                      </main>
                    </div>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <div>
                      <Navigation />
                      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                        <UserProfile />
                      </main>
                    </div>
                  }
                />
                <Route
                  path="/goals"
                  element={
                    <div>
                      <Navigation />
                      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                        <GoalsPage />
                      </main>
                    </div>
                  }
                />
                <Route path="/youtube" element={<YoutubePage />} />
                <Route path="/articles" element={<ArticlesPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/government-schemes" element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    {React.createElement(require('./pages/GovernmentSchemesPage').default)}
                  </React.Suspense>
                } />
                <Route path="/government-schemes/:schemeName" element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    {React.createElement(require('./pages/GovernmentSchemesPage').default)}
                  </React.Suspense>
                } />

                {/* Error Routes */}
                <Route path="/500" element={<ErrorPage code={500} message="Internal Server Error" />} />
                <Route path="/403" element={<ErrorPage code={403} message="Access Forbidden" />} />
                <Route path="/maintenance" element={<ErrorPage code={503} message="Service Temporarily Unavailable" />} />
                <Route path="*" element={<ErrorPage />} /> {/* 404 catch-all route */}
                <Route path="/dashboard" element={<InvestorDashboard />} />
              </Routes>
              <Footer />
              <ChatBotButton />
              {/* <TalkToMukeshButton /> */}
            </>
          </Suspense>
        </ErrorBoundary>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
